import type { StatusTone } from "@playbookai/ui";
import type {
  KpiDirection,
  KpiLifecycleStatus,
  KpiMeasurementStatus,
  KpiPerformanceStatus,
  KpiReadiness,
  ManagedKpiReportingFrequency,
} from "../api/types.js";

/**
 * Presentation maps for the KPI Manager — the three independent status
 * dimensions (KPI Ecosystem §6) plus direction. Labels are product copy; tones
 * are the DS `StatusTone` vocabulary. A fresh KPI reads
 * "Active · Not Connected · Not Available" — `not_connected`/`not_available` are
 * the NORMAL default (the data plane is deferred), rendered `neutral`, never as
 * an error.
 */
export const LIFECYCLE_LABEL: Record<KpiLifecycleStatus, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

export const LIFECYCLE_TONE: Record<KpiLifecycleStatus, StatusTone> = {
  draft: "neutral",
  active: "healthy",
  archived: "neutral",
};

export const MEASUREMENT_LABEL: Record<KpiMeasurementStatus, string> = {
  not_connected: "Not Connected",
  current: "Current",
  stale: "Stale",
  unavailable: "Unavailable",
};

export const MEASUREMENT_TONE: Record<KpiMeasurementStatus, StatusTone> = {
  not_connected: "neutral",
  current: "healthy",
  stale: "watch",
  unavailable: "critical",
};

/** Why a KPI is / isn't measurable — the honest signal in place of the deferred
 * data-product mapping surface. */
export const MEASUREMENT_HINT: Record<KpiMeasurementStatus, string> = {
  not_connected:
    "No data source connected yet — measurement populates once a data product is bound.",
  current: "Recent trusted data is available.",
  stale: "Last known data is older than the allowed freshness window.",
  unavailable: "A source exists but can't currently provide trustworthy data.",
};

export const PERFORMANCE_LABEL: Record<KpiPerformanceStatus, string> = {
  not_available: "Not Available",
  green: "On Track",
  amber: "At Risk",
  red: "Off Track",
};

export const PERFORMANCE_TONE: Record<KpiPerformanceStatus, StatusTone> = {
  not_available: "neutral",
  green: "healthy",
  amber: "watch",
  red: "critical",
};

/**
 * PB-98 — the derived readiness headline. "Data Product Required" deliberately
 * reuses the copy PB-96 established for the equivalent TTI state, so a user
 * meets one phrase for one condition across Hoshin and the KPI Manager.
 */
export const READINESS_LABEL: Record<KpiReadiness, string> = {
  active: "Active",
  inactive: "Inactive",
  data_required: "Data Product Required",
  stale: "Stale",
  unavailable: "Unavailable",
};

/** `data_required` is `neutral`, NOT `critical` — with the data plane deferred
 * it is the normal state of almost every KPI, and colouring it as an error
 * would train users to ignore the badge. */
export const READINESS_TONE: Record<KpiReadiness, StatusTone> = {
  active: "healthy",
  inactive: "neutral",
  data_required: "neutral",
  stale: "watch",
  unavailable: "critical",
};

/** Why a KPI reads the way it does — the honest one-liner behind the pill. */
export const READINESS_HINT: Record<KpiReadiness, string> = {
  active: "Measured from a connected data product.",
  inactive:
    "Not an operational KPI yet — a draft or archived KPI shows no measured standing.",
  data_required:
    "No data product connected yet — this KPI can't be measured until one is bound.",
  stale: "Last known data is older than the allowed freshness window.",
  unavailable: "A source exists but can't currently provide trustworthy data.",
};

export const DIRECTION_LABEL: Record<KpiDirection, string> = {
  HIGHER_IS_BETTER: "Higher is better",
  LOWER_IS_BETTER: "Lower is better",
  TARGET_RANGE: "Target range",
};

/** PB-2941 — display labels for the closed reporting-cadence vocabulary. The
 * wire values are snake_case (`KPI_REPORTING_FREQUENCIES`); nothing outside
 * this map should render one. */
export const REPORTING_FREQUENCY_LABEL: Record<
  ManagedKpiReportingFrequency,
  string
> = {
  daily: "Daily",
  weekly: "Weekly",
  every_two_weeks: "Every two weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

/** Deterministic YYYY-MM-DD for list/detail timestamps (locale-independent). */
export function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

/** A full ISO date-time carrying an EXPLICIT zone — `Z` or `±HH:MM` / `±HHMM`.
 * Without one there is no instant to convert, only a wall clock. Components are
 * captured so the calendar can be validated before anything is converted. */
const ZONED_ISO_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

/** True when year-month-day names a day that exists. `Date.UTC` NORMALIZES an
 * impossible one (Feb 31 → Mar 3) rather than failing, so the probe's
 * components are compared back against what was asked for. */
function isRealCalendarDate(y: number, mo: number, d: number): boolean {
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return (
    probe.getUTCFullYear() === y &&
    probe.getUTCMonth() === mo - 1 &&
    probe.getUTCDate() === d
  );
}

/**
 * YYYY-MM-DD HH:mm (UTC) for an instant that has to be readable to the MINUTE —
 * "last observed" on the KPI detail, where the date alone cannot distinguish two
 * readings taken the same day.
 *
 * UTC rather than localised, for the same reason {@link formatDate} is: the KPI
 * surfaces are deterministic under test and across locales, and the suffix says
 * which clock it is so the reader is not left guessing.
 *
 * CONVERTED, not sliced. This used to take the first sixteen characters and
 * append " UTC", which is only correct for a `Z` instant — the shape
 * `toISOString()` emits and therefore the only one anyone had looked at. An
 * offset-qualified value (`…T09:05:00+05:30`, which the `date-time` contract
 * permits and a non-Prisma producer could emit) rendered its LOCAL wall clock
 * under a UTC label, off by the offset: 09:05 UTC for an instant that is really
 * 03:35 UTC. Silently wrong is the worst outcome for a timestamp whose whole job
 * is to order two readings taken the same day.
 *
 * Falls back to the raw string for anything that is not a zoned ISO instant —
 * including a zone-LESS date-time, which is a wall clock and not something this
 * function may relabel as UTC, and a CALENDAR-IMPOSSIBLE one. The shape check
 * alone admitted `2026-02-31T10:00:00Z`, which `new Date` silently normalizes
 * to March 3rd — a date the string never named, formatted as though the wire
 * had said it. The written components are range-checked first, so an invalid
 * date renders truthfully raw rather than shifted.
 */
export function formatDateTime(iso: string): string {
  const match = ZONED_ISO_INSTANT.exec(iso);
  if (!match) return iso;
  const [, year, month, day, hours, minutes, seconds] = match;
  if (
    !isRealCalendarDate(Number(year), Number(month), Number(day)) ||
    Number(hours) > 23 ||
    Number(minutes) > 59 ||
    Number(seconds ?? "0") > 59
  ) {
    return iso;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-` +
    `${pad(parsed.getUTCDate())} ` +
    `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())} UTC`
  );
}

/**
 * The value type a unit implies, worded as the design canvas words the KPI
 * "Type" column (Percentage, Count, Duration, Score, Amount). Derived from the
 * unit alone, so it is always true of the KPI; unknown units show as themselves.
 */
export function valueTypeLabel(unit: string): string {
  const u = unit.trim();
  const lower = u.toLowerCase();
  if (!u) return "—";
  if (/%|percent/.test(lower)) return "Percentage";
  if (
    /^(count|counts|units?|items?|defects?|issues?|tickets?|events?|ppm|dpmo|pcs|pieces?)$/.test(
      lower,
    )
  )
    return "Count";
  // A currency symbol or ISO code is the stronger signal: "$/day" and
  // "USD/month" are amounts per period, not durations.
  if (/€|\$|£|usd|eur|gbp|inr|currency|cost|amount/.test(lower))
    return "Amount";
  if (
    /hour|day|minute|second|week|month|year|time|duration|min$|hrs?$|sec$/.test(
      lower,
    )
  )
    return "Duration";
  if (/score|nps|index|rating|points?$|^pts$/.test(lower)) return "Score";
  return u.charAt(0).toUpperCase() + u.slice(1);
}
